// HELP-48771 - stop a form being submitted twice.
//
// Under a slow response nothing told the user their click had registered, so
// they clicked again and the server received two POSTs (two registrations, two
// competition entries, ...). This locks every form site-wide on submit: repeat
// submits are swallowed and the submit button shows a Bootstrap spinner until
// the request finishes.
//
// The listener is on `document` in the CAPTURE phase, which matters: forms wired
// up with jQuery Validate return `false` from their submit handler, and jQuery
// turns that into preventDefault() + stopPropagation(), so a bubble-phase
// listener would never see those forms at all.
//
// Running before everyone else means we do not yet know whether the submit is
// real -- validation may be about to cancel it. So the form is marked pending
// straight away (that is the bit that has to be synchronous, or two submits in
// the same task both get through) and the decision about what to *show* is
// deferred to the end of the current task, by which point every other handler
// has run and the three cases are distinguishable:
//
//   not cancelled            -> a normal POST, the page is navigating   -> busy
//   cancelled, XHR started   -> an AJAX submit is in flight             -> busy,
//                               release when the request settles
//   cancelled, no XHR        -> validation (or something else) said no  -> undo
//
// Deferring the button state also keeps the POST correct: disabling a named
// submit button inside the submit event would drop it from the form's entry
// list, and multi-step forms (simple.html, micro-form.html, contact-support.html)
// rely on which button was pressed.
//
// A submit that never fires a submit event -- HTMLFormElement.prototype.submit()
// called from script -- is caught by wrapping that method instead (see below).
// `users/register.html` and Coredna's invisible reCAPTCHA both finish that way.
//
// The AJAX case is spotted through jQuery's global `ajaxSend`, so a submit
// handler that sends with `fetch()` instead looks like a cancelled submit with
// no request: the lock is undone a tick later rather than held for the response.
//
// Invisible reCAPTCHA is the one cancelled-with-no-request case that must NOT be
// undone. Coredna renders a Captcha form element as:
//
//   form.onsubmit = function () { grecaptcha.execute(); return false; };
//   <callback>    = function () { form.submit(); };
//
// so the submit event is cancelled, grecaptcha is not jQuery AJAX (no `ajaxSend`),
// and the real POST arrives later through the native submit(). Undoing the lock
// there left the button live for the whole challenge -- every extra click ran
// grecaptcha.execute() again and produced another POST, which is the exact bug
// this module exists to stop.

/** Buttons that are icon-only site search triggers - a spinner there is noise. */
const EXCLUDED_SUBMITTERS = '.btn--search';

/** Escape hatch, usable on either the form or its submit button. */
const OPT_OUT = 'data-no-submit-lock';

/**
 * A full-page POST normally ends in navigation, so the lock does not need
 * releasing. If the server stalls, or answers with a file download (which leaves
 * the page in place), release anyway rather than stranding the user.
 */
const SAFETY_RELEASE_MS = 15000;

/**
 * An invisible reCAPTCHA cancels the submit event, runs its challenge, then posts
 * the form itself. While one is running the lock is held rather than undone.
 */
const INVISIBLE_CAPTCHA = '.g-recaptcha[data-size="invisible"]';

/**
 * An "invisible" reCAPTCHA still shows an image challenge when it is suspicious of
 * the visitor, and solving one takes far longer than a POST. Releasing on the
 * normal safety net would hand the button back mid-challenge, so that case gets a
 * longer leash -- still finite, because a captcha that errors must not strand the
 * user with a dead button.
 */
const CAPTCHA_RELEASE_MS = 120000;

/**
 * Marker class on the spinner we inject, so release() removes ours and only
 * ours. The button's own markup is never rewritten: a few templates
 * (base-template.html's sign-in form) hold references to nodes inside their
 * button and toggle them from AJAX callbacks, and replacing innerHTML would
 * detach those nodes underneath them.
 */
const SPINNER_CLASS = 'js-submit-lock-spinner';

const SPINNER_HTML = `<span class="spinner-border spinner-border-sm me-2 ${SPINNER_CLASS}" role="status" aria-hidden="true"></span>`
	+ `<span class="visually-hidden ${SPINNER_CLASS}">Submitting</span>`;

/**
 * Forms with a submit in progress. The value is the button we put into the busy
 * state, or null when there is nothing to undo -- so a button the page disabled
 * itself is never wrongly re-enabled.
 */
const pending = new Map();

/**
 * Forms whose native submit() we have already let through. A script-driven submit
 * fires no submit event, so `pending` alone cannot tell the captcha delivering its
 * one real POST apart from a second call that would duplicate it.
 */
const nativelySubmitted = new WeakSet();

/**
 * jqXHRs started while a submit event is being dispatched. Null the rest of the
 * time, so unrelated background AJAX is not mistaken for a form submission.
 */
let startedRequests = null;

$(document).on('ajaxSend', (event, jqXHR) => {
	if (startedRequests) {
		startedRequests.push(jqXHR);
	}
});

function isOptedOut(element) {
	return !!element && element.hasAttribute(OPT_OUT);
}

/**
 * The button to show the in-flight state on. `event.submitter` is the pressed
 * button; forms submitted with Enter, or via requestSubmit(), have none, so fall
 * back to the button the browser would have used.
 */
function resolveSubmitter(form, submitter) {
	return submitter || form.querySelector('button:not([type]), button[type="submit"], input[type="submit"]');
}

/** Puts the pressed button into its in-flight state. */
function showBusy(form, submitter) {
	// A button that was already disabled (register.html's Finish button, the
	// profile page's edit buttons) is somebody else's to re-enable, so leave it.
	if (!submitter || submitter.disabled) {
		return;
	}

	// <input type="submit"> holds no children, and a button whose template
	// already ships its own spinner does not need a second one.
	if (submitter.tagName === 'BUTTON' && !submitter.querySelector('.spinner-border')) {
		submitter.insertAdjacentHTML('afterbegin', SPINNER_HTML);
	}

	submitter.disabled = true;
	submitter.setAttribute('aria-busy', 'true');
	submitter.setAttribute('aria-disabled', 'true');

	pending.set(form, submitter);
}

/**
 * Locks a form by hand. Only needed by code that submits without firing a submit
 * event; everything that goes through `form.submit()`-the-event is automatic.
 */
export function lock(form, submitter = null) {
	if (pending.has(form)) {
		return;
	}

	pending.set(form, null);
	showBusy(form, resolveSubmitter(form, submitter));
}

/** Undoes a lock, restoring the button if we were the one that changed it. */
export function release(form) {
	if (!pending.has(form)) {
		return;
	}

	const submitter = pending.get(form);

	pending.delete(form);
	nativelySubmitted.delete(form);

	if (!submitter) {
		return;
	}

	submitter.querySelectorAll(`.${SPINNER_CLASS}`).forEach((node) => node.remove());

	submitter.disabled = false;
	submitter.removeAttribute('aria-busy');
	submitter.removeAttribute('aria-disabled');
}

document.addEventListener('submit', (event) => {
	const form = event.target;

	if (!(form instanceof HTMLFormElement) || isOptedOut(form)) {
		return;
	}

	if (pending.has(form)) {
		// Repeat submit while the first is still running. Stopping propagation
		// as well as the default keeps AJAX submit handlers from firing too --
		// they are the ones that would create the duplicate record.
		event.preventDefault();
		event.stopPropagation();
		return;
	}

	const submitter = resolveSubmitter(form, event.submitter);

	if (isOptedOut(submitter) || (submitter && submitter.matches(EXCLUDED_SUBMITTERS))) {
		return;
	}

	pending.set(form, null);
	startedRequests = [];

	setTimeout(() => {
		const requests = startedRequests || [];

		startedRequests = null;

		// Cancelled with nothing sent: validation said no -- unless an invisible
		// reCAPTCHA is running, which cancels exactly the same way and then posts
		// the form itself once the challenge clears.
		const captchaRunning = event.defaultPrevented
			&& !requests.length
			&& !!form.querySelector(INVISIBLE_CAPTCHA);

		if (event.defaultPrevented && !requests.length && !captchaRunning) {
			pending.delete(form);
			return;
		}

		showBusy(form, submitter);

		if (requests.length) {
			let outstanding = requests.length;

			requests.forEach((jqXHR) => {
				jqXHR.always(() => {
					outstanding -= 1;

					if (outstanding === 0) {
						release(form);
					}
				});
			});

			return;
		}

		setTimeout(() => release(form), captchaRunning ? CAPTCHA_RELEASE_MS : SAFETY_RELEASE_MS);
	}, 0);
}, true); // capture - see the note at the top of the file

// HTMLFormElement.prototype.submit() posts without firing a submit event, so the
// listener above never sees it. Both scripted submitters on the site finish this
// way -- the reCAPTCHA callback and `users/register.html` -- and a second call
// would post a second time, so the first one through wins and the rest are
// swallowed until the form is released.
const nativeSubmit = HTMLFormElement.prototype.submit;

HTMLFormElement.prototype.submit = function submit(...args) {
	if (isOptedOut(this)) {
		return nativeSubmit.apply(this, args);
	}

	if (nativelySubmitted.has(this)) {
		return undefined;
	}

	nativelySubmitted.add(this);
	lock(this);

	return nativeSubmit.apply(this, args);
};

// Coming back to a locked page via the back button restores it from the bfcache
// exactly as it was left, spinner and all.
window.addEventListener('pageshow', (event) => {
	if (event.persisted) {
		Array.from(pending.keys()).forEach(release);
	}
});
